import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LoadingContent } from '../../../components/Loading';
import ListPageLayout from '../../../components/ListPageLayout';
import { useFetch } from '../../../hooks/useFecth';
import SelectClub from '../../../components/SelectClub';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';
import {
    BusinessOutlined,
    CheckCircleOutline,
    PersonOutline,
    ReceiptLongOutlined,
    ScheduleOutlined,
    Visibility,
} from '@mui/icons-material';
import { cashbackSx } from './CashbackLayoutSx';

const formatCurrency = value =>
    Number(value || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const formatDateTime = date => {
    if (!date) return '-';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

function DetailField({ label, value }) {
    return (
        <Box minWidth={0}>
            <Typography variant="caption" color="text.secondary" sx={cashbackSx.fieldLabel}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={500} title={value || '-'}>
                {value || '-'}
            </Typography>
        </Box>
    );
}

function CashoutDetailsModal({ open, onClose, cashoutId, onExecute }) {
    const { data, isLoading, mutate } = useFetch(cashoutId ? `cashouts/${cashoutId}` : null);
    const { setErrors, enqueueSnackbar } = useErrors();
    const [executing, setExecuting] = useState(false);

    const handleExecute = async () => {
        try {
            setExecuting(true);
            await server.post(`cashouts/${cashoutId}/execute`);
            enqueueSnackbar('Saque executado com sucesso!');
            await mutate();
            if (onExecute) onExecute();
        } catch (error) {
            setErrors(error);
        } finally {
            setExecuting(false);
        }
    };

    if (!data) return null;

    const cashout = data;
    const isExecuted = Boolean(cashout.transferedAt);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: cashbackSx.modalPaper }}
        >
            <DialogTitle sx={{ pb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={cashbackSx.modalHeaderIcon}>
                            <ReceiptLongOutlined fontSize="small" />
                        </Box>
                        <Stack spacing={0.25}>
                            <Typography variant="h6" fontWeight={600}>
                                Detalhes do saque
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Solicitação #{cashout.id}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Chip
                        label={isExecuted ? 'Executado' : 'Pendente'}
                        color={isExecuted ? 'success' : 'warning'}
                        size="small"
                        variant="outlined"
                    />
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
                {isLoading ? (
                    <LoadingContent />
                ) : (
                    <Stack spacing={2.5}>
                        <Box sx={cashbackSx.modalAmountBox}>
                            <Typography sx={{ ...cashbackSx.modalSectionTitle, mb: 0.5 }}>
                                Valor total do saque
                            </Typography>
                            <Typography variant="h4" fontWeight={700} sx={cashbackSx.amountText}>
                                R$ {formatCurrency(cashout.amount)}
                            </Typography>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Box sx={{ ...cashbackSx.modalSection, flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                                    <PersonOutline sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    <Typography sx={{ ...cashbackSx.modalSectionTitle, mb: 0 }}>
                                        Assinante
                                    </Typography>
                                </Stack>
                                <Stack spacing={1.5}>
                                    <DetailField label="Nome" value={cashout.person?.name} />
                                    <DetailField label="E-mail" value={cashout.person?.email} />
                                    <DetailField label="Documento" value={cashout.person?.document} />
                                </Stack>
                            </Box>

                            <Box sx={{ ...cashbackSx.modalSection, flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                                    <BusinessOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    <Typography sx={{ ...cashbackSx.modalSectionTitle, mb: 0 }}>
                                        Clube
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" fontWeight={500}>
                                    {cashout.club?.name || cashout.club?.businessName || '-'}
                                </Typography>
                            </Box>
                        </Stack>

                        <Box sx={cashbackSx.modalSection}>
                            <Typography sx={cashbackSx.modalSectionTitle}>
                                Resgates inclusos ({cashout.rescues?.length || 0})
                            </Typography>
                            <TableContainer
                                component={Paper}
                                elevation={0}
                                variant="outlined"
                                sx={{ borderRadius: 1, overflow: 'hidden' }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={cashbackSx.tableHeadRow}>
                                            <TableCell>Oferta</TableCell>
                                            <TableCell>Anunciante</TableCell>
                                            <TableCell align="right">Comissão</TableCell>
                                            <TableCell>Data validação</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cashout.rescues?.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        textAlign="center"
                                                        py={2}
                                                    >
                                                        Nenhum resgate encontrado
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            cashout.rescues?.map(rescue => (
                                                <TableRow key={rescue.id} sx={cashbackSx.tableRow}>
                                                    <TableCell>{rescue.offer?.name || '-'}</TableCell>
                                                    <TableCell>
                                                        {rescue.advertiser?.name ||
                                                            rescue.advertiser?.businessName ||
                                                            '-'}
                                                    </TableCell>
                                                    <TableCell align="right" sx={cashbackSx.amountText}>
                                                        R$ {formatCurrency(rescue.saleCommision)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDateTime(rescue.validatedAt ?? rescue.verifyAt)}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            sx={cashbackSx.modalTimeline}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
                                <Box sx={cashbackSx.modalTimelineIcon}>
                                    <ScheduleOutlined sx={{ fontSize: 18 }} />
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Solicitação
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {formatDateTime(cashout.createdAt)}
                                    </Typography>
                                </Box>
                            </Stack>

                            {isExecuted && (
                                <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
                                    <Box sx={cashbackSx.modalTimelineIcon}>
                                        <CheckCircleOutline sx={{ fontSize: 18 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Execução
                                        </Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {formatDateTime(cashout.transferedAt)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    Fechar
                </Button>
                {!isExecuted && (
                    <Button onClick={handleExecute} variant="contained" disabled={executing}>
                        {executing ? 'Executando...' : 'Executar saque'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default function Cashback() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
    const [statusValue, setStatusValue] = useState(searchParams.get('status') || '');
    const [clubId, setClubId] = useState(searchParams.get('clubId') || '');
    const [fromDate, setFromDate] = useState(searchParams.get('from') || '');
    const [toDate, setToDate] = useState(searchParams.get('to') || '');
    const [selectedCashout, setSelectedCashout] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const { enqueueSnackbar } = useErrors();

    const params = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);

    const { data, isFetching, mutate } = useFetch('cashouts', { params });

    const rows = data?.rows ?? [];

    const handleClearFilters = () => {
        setSearchParams({});
        setSearchValue('');
        setStatusValue('');
        setClubId('');
        setFromDate('');
        setToDate('');
    };

    const handlePageChange = (_, page) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('page', page);
        setSearchParams(nextParams);
    };

    const handlePageSizeChange = nextSize => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('per_page', String(nextSize));
        nextParams.set('page', '1');
        setSearchParams(nextParams);
    };

    const handleOpenDetails = cashoutId => {
        setSelectedCashout(cashoutId);
        setDetailsOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedCashout(null);
    };

    const handleExecuteSuccess = () => {
        mutate();
    };

    const hasFilters = Array.from(searchParams.entries()).some(
        ([key, value]) => value !== '' && value !== null && key !== 'page' && key !== 'per_page',
    );

    const pageSize = Number(searchParams.get('per_page')) || 10;

    const applyFilters = close => {
        const nextParams = new URLSearchParams();
        if (searchValue) nextParams.set('search', searchValue);
        if (statusValue) nextParams.set('status', statusValue);
        if (clubId) nextParams.set('clubId', clubId);
        if (fromDate) nextParams.set('from', fromDate);
        if (toDate) nextParams.set('to', toDate);
        if (pageSize !== 10) nextParams.set('per_page', String(pageSize));
        setSearchParams(nextParams);
        close?.();
    };

    return (
        <>
            <ListPageLayout
                title="Cashback"
                subtitle="Solicitações de saque de cashback dos assinantes."
                counts={[{ label: 'solicitações', value: data?.numRows ?? 0, color: 'primary' }]}
                loading={isFetching && !data}
                filterActive={hasFilters}
                onClearFilters={hasFilters ? handleClearFilters : null}
                viewStorageKey="admin-cashback-view"
                isEmpty={!isFetching && rows.length === 0}
                empty={
                    <Stack alignItems="center" spacing={1}>
                        <Typography variant="body1">Nenhuma solicitação encontrada.</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Ajuste os filtros ou tente novamente mais tarde.
                        </Typography>
                    </Stack>
                }
                pagination={{
                    page: data?.page || 1,
                    pages: data?.pages || 1,
                    total: data?.numRows ?? 0,
                    pageSize,
                    onChange: handlePageChange,
                    onPageSizeChange: handlePageSizeChange,
                    itemLabel: (data?.numRows ?? 0) === 1 ? 'solicitação' : 'solicitações',
                }}
                filterPanel={({ close }) => (
                    <Stack
                        spacing={2}
                        component="form"
                        onSubmit={e => {
                            e.preventDefault();
                            applyFilters(close);
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            label="Buscar assinante"
                            placeholder="Nome, e-mail ou documento"
                            value={searchValue}
                            onChange={event => setSearchValue(event.target.value)}
                        />
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Status"
                            value={statusValue}
                            onChange={event => setStatusValue(event.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="PENDING">Pendente</MenuItem>
                            <MenuItem value="EXECUTED">Executado</MenuItem>
                        </TextField>
                        <SelectClub
                            fullWidth
                            size="small"
                            value={clubId}
                            name="clubId"
                            onChange={event => setClubId(event.target.value || '')}
                        />
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                size="small"
                                label="De"
                                type="date"
                                value={fromDate}
                                onChange={event => setFromDate(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Até"
                                type="date"
                                value={toDate}
                                onChange={event => setToDate(event.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Stack>
                        <Button type="submit" variant="contained" fullWidth>
                            Aplicar filtros
                        </Button>
                    </Stack>
                )}
            >
                {({ viewMode }) =>
                    !data && isFetching ? (
                        <LoadingContent />
                    ) : (
                        <Box
                            sx={{
                                p: 2,
                                display: viewMode === 'cards' ? 'grid' : 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                gridTemplateColumns:
                                    viewMode === 'cards'
                                        ? { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }
                                        : undefined,
                            }}
                        >
                            {rows.map(cashout => {
                                const isExecuted = Boolean(cashout.transferedAt);
                                const personName = cashout.person?.name || 'Não informado';
                                const clubName = cashout.club?.name || cashout.club?.businessName || '—';

                                if (viewMode === 'cards') {
                                    return (
                                        <Card key={cashout.id} variant="outlined" sx={{ p: 2 }}>
                                            <Stack spacing={1.25}>
                                                <Chip
                                                    size="small"
                                                    label={isExecuted ? 'Executado' : 'Pendente'}
                                                    color={isExecuted ? 'success' : 'warning'}
                                                    variant="outlined"
                                                    sx={{ alignSelf: 'flex-start' }}
                                                />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Assinante
                                                    </Typography>
                                                    <Typography fontWeight={700}>{personName}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Clube
                                                    </Typography>
                                                    <Typography fontWeight={600}>{clubName}</Typography>
                                                </Box>
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Typography fontWeight={700}>
                                                        R$ {formatCurrency(cashout.amount)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDateTime(cashout.createdAt)}
                                                    </Typography>
                                                </Stack>
                                                <Button
                                                    size="small"
                                                    startIcon={<Visibility />}
                                                    onClick={() => handleOpenDetails(cashout.id)}
                                                >
                                                    Ver detalhes
                                                </Button>
                                            </Stack>
                                        </Card>
                                    );
                                }

                                return (
                                    <Card key={cashout.id} variant="outlined">
                                        <Stack
                                            direction={{ xs: 'column', md: 'row' }}
                                            alignItems={{ xs: 'stretch', md: 'center' }}
                                            spacing={2}
                                            px={2.5}
                                            py={1.75}
                                        >
                                            <Chip
                                                size="small"
                                                label={isExecuted ? 'Executado' : 'Pendente'}
                                                color={isExecuted ? 'success' : 'warning'}
                                                variant="outlined"
                                                sx={{ minWidth: 100 }}
                                            />
                                            <Box sx={{ flex: 1.3, minWidth: 0 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Assinante
                                                </Typography>
                                                <Typography fontWeight={700} noWrap>
                                                    {personName}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Clube
                                                </Typography>
                                                <Typography fontWeight={600} noWrap>
                                                    {clubName}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: '0 0 110px' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Valor
                                                </Typography>
                                                <Typography fontWeight={700}>
                                                    R$ {formatCurrency(cashout.amount)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: '0 0 140px' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Data
                                                </Typography>
                                                <Typography fontWeight={600}>
                                                    {formatDateTime(cashout.createdAt)}
                                                </Typography>
                                            </Box>
                                            <Button
                                                size="small"
                                                startIcon={<Visibility />}
                                                onClick={() => handleOpenDetails(cashout.id)}
                                            >
                                                Detalhes
                                            </Button>
                                        </Stack>
                                    </Card>
                                );
                            })}
                        </Box>
                    )
                }
            </ListPageLayout>

            <CashoutDetailsModal
                open={detailsOpen}
                onClose={handleCloseDetails}
                cashoutId={selectedCashout}
                onExecute={handleExecuteSuccess}
            />
        </>
    );
}

